import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { testIds } from '@/lib/test-ids'
import { AuthLayout } from './auth-layout'
import { AuthFormAlert } from './auth-form-alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const forgotSchema = z.object({
  email: z.string().email('Enter a valid email'),
})

type ForgotFormValues = z.infer<typeof forgotSchema>

export function ForgotPasswordForm() {
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = async (values: ForgotFormValues) => {
    if (!supabase) return
    setGeneralError(null)
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) {
      setGeneralError(error.message)
      return
    }
    setSent(true)
  }

  if (sent) {
    return (
      <AuthLayout
        title="Reset your password"
        description="If an account exists for this email, we've sent a reset link."
      >
        <p className="text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="text-primary underline-offset-4 hover:underline"
          >
            Back to log in
          </Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter your email and we'll send you a reset link."
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          data-testid={testIds.auth.forgotPasswordForm}
        >
          {generalError && <AuthFormAlert message={generalError} />}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Send reset link'
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Waiting on an invite, not a reset? Open the expired invite link and
            use Send a new invite.
          </p>
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/login"
              className="text-primary underline-offset-4 hover:underline"
            >
              Back to log in
            </Link>
          </p>
        </form>
      </Form>
    </AuthLayout>
  )
}
